<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Play Settings</span>
    </template>

    <template #actions>
      <router-link :to="`/sales-plays/${playId}`" class="backLink">
        <AppIcon name="arrow_right" :size="14" class="backArrow" />
        Back to play
      </router-link>
      <span v-if="saving" class="autoSaveIndicator">Saving&hellip;</span>
      <span v-else-if="showSaved" class="autoSaveIndicator saved">Saved</span>
    </template>

    <div v-if="loading" class="pageLoading">Loading&hellip;</div>
    <div v-else-if="loadError" class="errorBanner">{{ loadError }}</div>

    <div v-else class="settingsLayout">

      <!-- ── Ownership ── -->
      <section class="settingsSection">
        <h2 class="sectionHeading">Ownership</h2>
        <p class="sectionLead">
          Control which team member sends on behalf of each prospect.
        </p>

        <div class="field">
          <label class="fieldLabel">Mode</label>
          <SearchSelect
            v-model="form.ownership_mode"
            :options="ownershipModeOptions"
            hide-search
            :allow-clear="false"
          />
        </div>

        <!-- CRM Owner sub-fields -->
        <template v-if="form.ownership_mode === 'crm_owner'">
          <div class="field">
            <label class="fieldLabel">Strategy</label>
            <SearchSelect
              v-model="form.crm_owner_strategy"
              :options="ownershipStrategyOptions"
              hide-search
              :allow-clear="false"
            />
          </div>

          <div class="fieldRow">
            <div class="field">
              <label class="fieldLabel">When no CRM owner found</label>
              <SearchSelect
                v-model="form.crm_owner_on_no_owner"
                :options="fallbackActionOptions"
                hide-search
                :allow-clear="false"
              />
            </div>
            <div class="field">
              <label class="fieldLabel">When CRM owner is not an app user</label>
              <SearchSelect
                v-model="form.crm_owner_on_not_in_app"
                :options="fallbackActionOptions"
                hide-search
                :allow-clear="false"
              />
            </div>
          </div>

          <div v-if="needsFallbackUser" class="field">
            <label class="fieldLabel">Fallback user</label>
            <SearchSelect
              v-model="form.crm_owner_fallback_user_id"
              :options="userOptions"
              placeholder="Select user..."
              allow-clear
            />
          </div>

          <div v-if="needsFallbackUsers" class="field">
            <label class="fieldLabel">Fallback round robin level</label>
            <SearchSelect
              v-model="form.crm_owner_fallback_rr_level"
              :options="assignmentLevelOptions"
              hide-search
              :allow-clear="false"
            />
          </div>

          <div v-if="needsFallbackUsers" class="field">
            <label class="fieldLabel">Round robin fallback users</label>
            <div class="pillRow">
              <span v-if="fallbackUserPills.length === 0" class="pillMuted">All active senders</span>
              <span v-for="u in fallbackUserPills" :key="u.value" class="pill">{{ u.label }}</span>
              <button type="button" class="pillEditBtn" @click="openShuttle('crm_owner_fallback_users')">Edit</button>
            </div>
          </div>
        </template>

        <!-- Round Robin sub-fields -->
        <template v-if="form.ownership_mode === 'round_robin'">
          <div class="field">
            <label class="fieldLabel">Assignment level</label>
            <SearchSelect
              v-model="form.round_robin_assignment_level"
              :options="assignmentLevelOptions"
              hide-search
              :allow-clear="false"
            />
          </div>
          <div class="field">
            <label class="fieldLabel">Round robin users</label>
            <div class="pillRow">
              <span v-if="roundRobinUserPills.length === 0" class="pillMuted">All active senders</span>
              <span v-for="u in roundRobinUserPills" :key="u.value" class="pill">{{ u.label }}</span>
              <button type="button" class="pillEditBtn" @click="openShuttle('round_robin_user_ids')">Edit</button>
            </div>
          </div>
        </template>

        <!-- Fixed Sender sub-fields -->
        <template v-if="form.ownership_mode === 'fixed_sender'">
          <div class="field">
            <label class="fieldLabel">Always send as</label>
            <SearchSelect
              v-model="form.fixed_sender_user_id"
              :options="userOptions"
              placeholder="Select user..."
              :allow-clear="false"
            />
          </div>
        </template>
      </section>
    </div>

    <!-- User shuttle modal -->
    <Modal v-if="shuttleTarget" title="Select users" @close="cancelShuttle">
      <Shuttle
        :available-items="shuttleAvailable"
        :selected-items="shuttleSelected"
        empty-available-text="No more users available."
        empty-selected-text="No users selected — all active senders will be used."
        :show-reorder="false"
        @add="shuttleAdd"
        @remove="shuttleRemove"
      />
      <template #footer>
        <button class="btn secondary" type="button" @click="cancelShuttle">Cancel</button>
        <button class="btn primary" type="button" @click="applyShuttle">Apply</button>
      </template>
    </Modal>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import Modal from '../components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import Shuttle from '../../components/Shuttle.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'
import { isFlockPlay } from '../composables/usePlayEngine'

const route = useRoute()
const router = useRouter()
const { setScope, clearScope } = useChatScope()

const playId = computed(() => String(route.params.id || ''))

const loading = ref(true)
const loadError = ref('')
const saving = ref(false)
const showSaved = ref(false)
let savedTimer: ReturnType<typeof setTimeout> | null = null

const userOptions = ref<{ value: string; label: string }[]>([])

// ── Flat form ──────────────────────────────────────────────────────
const form = ref({
  // Ownership
  ownership_mode: 'none',
  // CRM owner
  crm_owner_strategy: 'contact_first',
  crm_owner_on_no_owner: 'round_robin',
  crm_owner_on_not_in_app: 'block',
  crm_owner_fallback_user_id: '',
  crm_owner_fallback_users: [] as string[],
  crm_owner_fallback_rr_level: 'contact',
  // Round robin
  round_robin_user_ids: [] as string[],
  round_robin_assignment_level: 'contact',
  // Fixed sender
  fixed_sender_user_id: '',
})

// ── Options ────────────────────────────────────────────────────────
const ownershipModeOptions = [
  { value: 'none', label: 'No ownership (any sender)' },
  { value: 'crm_owner', label: 'Honor CRM owner' },
  { value: 'round_robin', label: 'Round robin' },
  { value: 'fixed_sender', label: 'Fixed sender' },
]

const ownershipStrategyOptions = [
  { value: 'contact_first', label: 'Contact/Lead first, then Account' },
  { value: 'account_only', label: 'Account only' },
]

const fallbackActionOptions = [
  { value: 'round_robin', label: 'Round robin' },
  { value: 'fallback_user', label: 'Use fallback user' },
  { value: 'block', label: 'Block prospecting' },
]

const assignmentLevelOptions = [
  { value: 'account', label: 'Account — assign per account, contacts follow' },
  { value: 'contact', label: 'Contact — assign each contact independently' },
]

// ── Computed helpers ───────────────────────────────────────────────
const needsFallbackUser = computed(() => {
  return [
    form.value.crm_owner_on_no_owner,
    form.value.crm_owner_on_not_in_app,
  ].includes('fallback_user')
})

const needsFallbackUsers = computed(() => {
  return [
    form.value.crm_owner_on_no_owner,
    form.value.crm_owner_on_not_in_app,
  ].includes('round_robin')
})

// ── Pill display helpers ───────────────────────────────────────────
const roundRobinUserPills = computed(() =>
  form.value.round_robin_user_ids
    .map((id) => userOptions.value.find((u) => u.value === id))
    .filter(Boolean) as { value: string; label: string }[],
)

const fallbackUserPills = computed(() =>
  form.value.crm_owner_fallback_users
    .map((id) => userOptions.value.find((u) => u.value === id))
    .filter(Boolean) as { value: string; label: string }[],
)

// ── Shuttle modal state ───────────────────────────────────────────
type ShuttleField = 'round_robin_user_ids' | 'crm_owner_fallback_users'
const shuttleTarget = ref<ShuttleField | null>(null)
const shuttleDraft = ref<string[]>([])

const shuttleAvailable = computed(() =>
  userOptions.value.filter((u) => !shuttleDraft.value.includes(u.value)),
)
const shuttleSelected = computed(() =>
  shuttleDraft.value
    .map((id) => userOptions.value.find((u) => u.value === id))
    .filter(Boolean) as { value: string; label: string }[],
)

function openShuttle(field: ShuttleField) {
  shuttleTarget.value = field
  shuttleDraft.value = [...form.value[field]]
}

function shuttleAdd(values: string[]) {
  shuttleDraft.value = [...shuttleDraft.value, ...values]
}

function shuttleRemove(values: string[]) {
  const remove = new Set(values)
  shuttleDraft.value = shuttleDraft.value.filter((v) => !remove.has(v))
}

function applyShuttle() {
  if (shuttleTarget.value) {
    form.value[shuttleTarget.value] = [...shuttleDraft.value]
  }
  shuttleTarget.value = null
  shuttleDraft.value = []
}

function cancelShuttle() {
  shuttleTarget.value = null
  shuttleDraft.value = []
}

// ── Snapshot for auto-save change detection ────────────────────────
const baselineSnapshot = ref('')

function snapshot(): string {
  return JSON.stringify(form.value)
}

// ── Load ───────────────────────────────────────────────────────────
function loadFromPlay(play: any) {
  const cfg = play.config || {}
  const oc = cfg.ownership_config || {}
  form.value.ownership_mode = oc.mode || 'none'

  if (oc.mode === 'fixed_sender' && oc.fixed_sender) {
    form.value.fixed_sender_user_id = oc.fixed_sender.user_id || ''
  }
  if (oc.mode === 'round_robin' && oc.round_robin) {
    form.value.round_robin_user_ids = oc.round_robin.user_ids || []
    form.value.round_robin_assignment_level = oc.round_robin.assignment_level || 'contact'
  }
  if (oc.mode === 'crm_owner' && oc.crm_owner) {
    const co = oc.crm_owner
    form.value.crm_owner_strategy = co.strategy || 'contact_first'
    form.value.crm_owner_on_no_owner = co.on_no_crm_owner || 'round_robin'
    form.value.crm_owner_on_not_in_app = co.on_owner_not_in_app || 'block'
    form.value.crm_owner_fallback_user_id = co.fallback_user_id || ''
    form.value.crm_owner_fallback_users = co.fallback_users || []
    form.value.crm_owner_fallback_rr_level = co.fallback_round_robin_level || 'contact'
  }

  baselineSnapshot.value = snapshot()
}

async function loadPlay() {
  loading.value = true
  loadError.value = ''
  try {
    const resp: any = await apiFetch(`/sales-plays/${playId.value}`)
    const play = resp?.play
    if (!play) throw new Error('Play not found')
    // A Strategy play's settings are not edited here.
    if (isFlockPlay(play)) {
      await router.replace(`/sales-plays/${playId.value}`)
      return
    }
    loadFromPlay(play)
  } catch (e: any) {
    loadError.value = e?.message || 'Failed to load play'
  } finally {
    loading.value = false
  }
}

async function loadUsers() {
  try {
    const resp = await apiFetch('/settings/team_members')
    const members = Array.isArray(resp?.members) ? resp.members : []
    userOptions.value = members.map((m: any) => ({ value: m.id, label: m.name || m.email }))
  } catch {
    // not critical
  }
}

// ── Save ───────────────────────────────────────────────────────────
function ownershipConfigToJson(): Record<string, any> | null {
  const f = form.value
  if (f.ownership_mode === 'none') return { mode: 'none' }
  if (f.ownership_mode === 'fixed_sender') {
    return { mode: 'fixed_sender', fixed_sender: { user_id: f.fixed_sender_user_id } }
  }
  if (f.ownership_mode === 'round_robin') {
    return {
      mode: 'round_robin',
      round_robin: {
        user_ids: f.round_robin_user_ids || [],
        assignment_level: f.round_robin_assignment_level || 'contact',
      },
    }
  }
  // crm_owner
  const crm: Record<string, any> = {
    strategy: f.crm_owner_strategy,
    on_no_crm_owner: f.crm_owner_on_no_owner,
    on_owner_not_in_app: f.crm_owner_on_not_in_app,
  }
  if (needsFallbackUser.value && f.crm_owner_fallback_user_id) {
    crm.fallback_user_id = f.crm_owner_fallback_user_id
  }
  if (needsFallbackUsers.value && f.crm_owner_fallback_users?.length) {
    crm.fallback_users = f.crm_owner_fallback_users
  }
  if (needsFallbackUsers.value) {
    crm.fallback_round_robin_level = f.crm_owner_fallback_rr_level || 'contact'
  }
  return { mode: 'crm_owner', crm_owner: crm }
}

async function save() {
  saving.value = true
  showSaved.value = false
  if (savedTimer) { clearTimeout(savedTimer); savedTimer = null }
  try {
    const body: Record<string, any> = {
      update: {
        config: {
          ownership_config: ownershipConfigToJson(),
        },
      },
    }
    await apiFetch(`/sales-plays/${playId.value}`, { method: 'PATCH', body })
    baselineSnapshot.value = snapshot()
    showSaved.value = true
    if (savedTimer) clearTimeout(savedTimer)
    savedTimer = setTimeout(() => { showSaved.value = false }, 2000)
  } catch (e) {
    console.error('[sales-play-settings] save failed', e)
  } finally {
    saving.value = false
  }
}

// ── Auto-save watcher (800ms debounce) ─────────────────────────────
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => snapshot(),
  (cur) => {
    if (!baselineSnapshot.value) return
    if (cur === baselineSnapshot.value) return
    if (autoSaveTimer) clearTimeout(autoSaveTimer)
    autoSaveTimer = setTimeout(() => {
      autoSaveTimer = null
      save()
    }, 800)
  },
)

// ── Lifecycle ──────────────────────────────────────────────────────
onMounted(async () => {
  setScope({
    scopeType: 'sales_play',
    scopeSubjectId: playId.value,
    pageContext: { sales_play_id: playId.value },
  })
  await Promise.all([loadPlay(), loadUsers()])
})

onBeforeUnmount(() => {
  clearScope()
  if (autoSaveTimer) clearTimeout(autoSaveTimer)
  if (savedTimer) clearTimeout(savedTimer)
})
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: var(--lh-tight);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-tertiary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-text-primary); }
.backArrow { transform: rotate(180deg); }

.autoSaveIndicator {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-tertiary);
}
.autoSaveIndicator.saved { color: var(--color-accent); }

.pageLoading,
.errorBanner {
  margin-top: var(--space-7);
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  color: var(--color-text-secondary);
}
.errorBanner { color: var(--color-danger, #b91c1c); }

.settingsLayout {
  display: flex;
  flex-direction: column;
  gap: var(--space-7);
  max-width: 640px;
}

.settingsSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.sectionHeading {
  margin: 0;
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--color-border-subtle);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-bold);
  color: var(--color-text-primary);
}

.sectionLead {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.fieldLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  color: var(--color-text-secondary);
}

.fieldHint {
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  margin-top: var(--space-1);
}
.fieldHintWarn {
  color: var(--color-warning, #b45309);
}
.fieldHintWarn a {
  color: inherit;
  text-decoration: underline;
}

.fieldRow {
  display: flex;
  gap: var(--space-4);
}
.fieldRow > .field { flex: 1; min-width: 0; }

.checkField {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.checkField input[type="checkbox"] {
  margin-top: 2px;
  flex-shrink: 0;
}
.checkField span {
  color: var(--color-text-secondary);
  line-height: var(--lh-normal);
}

.pillRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) var(--space-2);
  background: var(--color-bg-subtle);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}

.pillMuted {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
}

.pillEditBtn {
  appearance: none;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-app);
  padding: var(--space-1) var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out),
              color var(--duration-fast) var(--ease-out);
}
.pillEditBtn:hover {
  background: var(--color-bg-subtle);
  color: var(--color-text-primary);
}

.textInput {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
  background: var(--color-bg-app);
  color: var(--color-text-primary);
  transition: border-color var(--duration-fast) var(--ease-out);
}
.textInput:focus {
  outline: none;
  border-color: var(--color-accent);
}
</style>
